import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Trash2,
  Inbox,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Sparkles,
  ExternalLink,
  Pencil,
  Check,
  X as XIcon,
} from "lucide-react";
import Navbar from "../components/Navbar";
import FairValueSourcesModal from "../components/FairValueSourcesModal";
import MarketRadar from "../components/MarketRadar";
import { api } from "../lib/api";
import {
  fmtMoney,
  fmtPct,
  verdictStyle,
  marginColor,
  MARKETS,
  normalizeTicker,
  marketFromTicker,
  displayTicker,
} from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

// Use the backend-computed consolidated fair value (median of all sources).
// Falls back to a local avg of analyst+morningstar if backend didn't supply it.
const getConsolidated = (s) => {
  if (s.consolidated_fair_value) return s.consolidated_fair_value;
  const vals = [s.analyst_target_mean, s.user_morningstar_fv].filter((v) => v);
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

const diffPct = (price, fv) => {
  if (!price || !fv) return null;
  return ((fv - price) / price) * 100;
};

const CONVICTION_STYLE = {
  HIGH_UNDERVALUED: { emoji: "🟢", bg: "bg-moss text-bone-50 border-moss", label: "HIGH-CONVICTION UNDERVALUED" },
  HIGH_OVERVALUED:  { emoji: "🔴", bg: "bg-terracotta text-bone-50 border-terracotta", label: "CONSENSUS OVERVALUED" },
  MIXED_SIGNAL:     { emoji: "🟡", bg: "bg-amber_brand-bg text-amber_brand border-amber_brand/50", label: "MIXED SIGNAL" },
  MIXED_BULLISH:    { emoji: "🟢", bg: "bg-moss-bg text-moss-light border-moss-light/50", label: "MIXED BULLISH" },
  MIXED_BEARISH:    { emoji: "🟠", bg: "bg-terracotta-bg text-terracotta border-terracotta/40", label: "MIXED BEARISH" },
  LOW_COVERAGE:     { emoji: "⚪", bg: "bg-bone-100 text-stone_brand border-line", label: "LOW COVERAGE" },
  NEUTRAL:          { emoji: "⚪", bg: "bg-bone-100 text-stone_brand border-line", label: "NEUTRAL" },
};

function ConvictionBadge({ conviction }) {
  if (!conviction) return null;
  const s = CONVICTION_STYLE[conviction.level] || CONVICTION_STYLE.NEUTRAL;
  return (
    <div
      className={`inline-flex flex-col items-start border rounded-sm px-2 py-1 ${s.bg}`}
      title={conviction.detail}
      data-testid={`conviction-${conviction.level}`}
    >
      <span className="text-[0.55rem] font-mono uppercase tracking-widest leading-tight font-medium whitespace-nowrap">
        {s.emoji} {s.label}
      </span>
      {conviction.spread_pct !== undefined && (
        <span className="text-[0.55rem] font-mono opacity-75 leading-tight">
          {conviction.source_count} sources · spread {conviction.spread_pct}%
        </span>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [tickerInput, setTickerInput] = useState("");
  const [market, setMarket] = useState("NSE");
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [sortBy, setSortBy] = useState("diff");
  const [sortDir, setSortDir] = useState("desc"); // high to low by default
  const [sourcesStock, setSourcesStock] = useState(null); // for modal
  const [minSources3, setMinSources3] = useState(false);
  const [editingMS, setEditingMS] = useState(null); // ticker being edited
  const [editValue, setEditValue] = useState("");
  const [savingMS, setSavingMS] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/watchlist");
      setStocks(data.stocks || []);
    } catch {
      toast.error("Failed to load scanner");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Debounced ticker / company-name autosuggest (NSE master)
  useEffect(() => {
    const q = (tickerInput || "").trim();
    if (q.length < 2 || market !== "NSE") {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/stocks/search?q=${encodeURIComponent(q)}`);
        if (cancelled) return;
        setSuggestions(data.results || []);
        setShowSuggestions((data.results || []).length > 0);
        setActiveSuggestion(-1);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [tickerInput, market]);

  const pickSuggestion = (s) => {
    setTickerInput(s.symbol);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const onInputKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeSuggestion >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeSuggestion]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleAdd = async (e) => {
    e?.preventDefault();
    const t = normalizeTicker(tickerInput, market);
    if (!t || adding) return;
    setAdding(true);
    try {
      await api.post("/watchlist", { ticker: t });
      toast.success(`${displayTicker(t)} added`);
      setTickerInput("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add stock");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (ticker) => {
    try {
      await api.delete(`/watchlist/${encodeURIComponent(ticker)}`);
      setStocks((s) => s.filter((x) => x.ticker !== ticker));
      toast.success(`${displayTicker(ticker)} removed`);
    } catch {
      toast.error("Could not remove stock");
    }
  };

  const startEditMS = (s) => {
    setEditingMS(s.ticker);
    setEditValue(s.user_morningstar_fv ? String(s.user_morningstar_fv) : "");
  };

  const cancelEditMS = () => {
    setEditingMS(null);
    setEditValue("");
  };

  const saveEditMS = async (ticker) => {
    if (savingMS) return;
    const trimmed = editValue.trim();
    const value = trimmed === "" ? null : Number(trimmed);
    if (value !== null && (Number.isNaN(value) || value <= 0)) {
      toast.error("Enter a positive number, or leave blank to clear");
      return;
    }
    setSavingMS(true);
    try {
      await api.put(
        `/watchlist/${encodeURIComponent(ticker)}/morningstar-fv`,
        { value }
      );
      toast.success(value === null ? "Override cleared" : `Saved ₹${value} for ${displayTicker(ticker)}`);
      setEditingMS(null);
      setEditValue("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSavingMS(false);
    }
  };

  // Enrich + sort
  const rows = useMemo(() => {
    const enriched = stocks.map((s) => {
      const avg = getConsolidated(s);
      const sourceCount = (s.fair_value_sources || []).filter(
        (x) => x.type === "fair_value" || x.type === "analyst_target"
      ).length;
      return {
        ...s,
        consolidated_fv: avg,
        source_count: sourceCount || (avg ? 1 : 0),
        diff: s.consolidated_diff_percent ?? diffPct(s.current_price, avg),
        diff_analyst: diffPct(s.current_price, s.analyst_target_mean),
        diff_morningstar: diffPct(s.current_price, s.user_morningstar_fv),
        tickertape_target: s.tickertape?.price_target || null,
        diff_tickertape: diffPct(s.current_price, s.tickertape?.price_target),
      };
    });

    const dir = sortDir === "asc" ? 1 : -1;
    const pool = minSources3 ? enriched.filter((r) => r.source_count >= 3) : enriched;
    return [...pool].sort((a, b) => {
      const va = a[sortBy];
      const vb = b[sortBy];
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      if (typeof va === "string") return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
  }, [stocks, sortBy, sortDir, minSources3]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  return (
    <div className="min-h-screen bg-bone-50" data-testid="dashboard-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {/* Header */}
        <div className="mb-10">
          <div className="overline mb-3">— Fair Value Scanner</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone_brand-dark tracking-tight font-semibold">
            Find what's mispriced.
          </h1>
          <p className="text-stone_brand mt-2 max-w-2xl">
            Add any ticker. See live price next to fair value aggregated from analyst consensus,
            Morningstar, and your worksheet. Sort by the spread to find the biggest opportunities.
          </p>
        </div>

        {/* Market-wide daily radar: India + US */}
        <MarketRadar />

        {/* Add ticker form */}
        <form
          onSubmit={handleAdd}
          className="bg-white border border-line rounded-sm p-5 mb-8 flex flex-col gap-3"
          data-testid="scanner-add-form"
        >
          <div className="flex items-center gap-1.5" data-testid="market-selector">
            {MARKETS.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMarket(m.key)}
                className={`px-3 py-1 rounded-sm text-xs font-mono tracking-wider transition-colors ${
                  market === m.key
                    ? "bg-moss text-bone-50"
                    : "bg-white border border-line text-stone_brand hover:border-moss/50"
                }`}
                data-testid={`market-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone_brand-light" />
              <Input
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={onInputKeyDown}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder={
                  market === "NSE"
                    ? "Type ticker or company (e.g. RELIANCE, Adani Ports)"
                    : MARKETS.find((m) => m.key === market)?.hint
                }
                className="pl-9 rounded-sm border-line bg-white font-mono h-11 focus-visible:ring-moss"
                data-testid="ticker-input"
                autoComplete="off"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div
                  className="absolute left-0 right-0 top-full mt-1 bg-white border border-line rounded-sm shadow-lg z-30 max-h-80 overflow-y-auto"
                  data-testid="ticker-suggestions"
                >
                  {suggestions.map((s, i) => (
                    <button
                      type="button"
                      key={s.symbol}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickSuggestion(s);
                      }}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left border-b border-line last:border-0 transition-colors ${
                        i === activeSuggestion ? "bg-moss-bg" : "hover:bg-bone-100"
                      }`}
                      data-testid={`suggestion-${s.symbol}`}
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-sm font-semibold text-stone_brand-dark">
                          {s.symbol}
                        </div>
                        <div className="text-xs text-stone_brand truncate">{s.name}</div>
                      </div>
                      <span className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1.5 py-0.5 rounded-sm flex-shrink-0">
                        NSE
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={adding || !tickerInput.trim()}
              className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-11 px-5 gap-1.5"
              data-testid="add-stock-btn"
            >
              <Plus className="w-4 h-4" /> Add to Scanner
            </Button>
          </div>
          <p className="text-[0.7rem] font-mono uppercase tracking-widest text-stone_brand-light">
            — Sources: yfinance live · Yahoo analyst consensus · TickerTape forecasts · Morningstar (your worksheet) · Auto-refreshed daily
          </p>
        </form>

        {/* Table */}
        {loading ? (
          <div className="grid gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 bg-white border border-line rounded-sm animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-line rounded-sm p-12 text-center" data-testid="empty-scanner">
            <Inbox className="w-8 h-8 text-stone_brand-light mx-auto mb-4" />
            <div className="font-serif-display text-xl text-stone_brand-dark mb-2">
              Scanner is empty
            </div>
            <p className="text-sm text-stone_brand">
              Add a ticker above (try <span className="font-mono">RELIANCE</span>,{" "}
              <span className="font-mono">HDFCBANK</span>, <span className="font-mono">AAPL</span>) to start tracking
              live price vs fair value.
            </p>
            <Button
              onClick={() => navigate("/demo")}
              variant="outline"
              className="rounded-sm border-moss text-moss hover:bg-moss-bg mt-5"
              data-testid="cta-go-demo"
            >
              Or browse "My Universe" (98 pre-loaded stocks)
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="overline">
                {rows.length} {rows.length === 1 ? "Stock" : "Stocks"} · Sorted by {sortBy === "diff" ? "Diff" : sortBy} {sortDir === "desc" ? "↓ High → Low" : "↑ Low → High"}
              </div>
              <button
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                className="text-xs font-mono tracking-wider text-moss hover:text-moss-hover flex items-center gap-1"
                data-testid="flip-sort-btn"
              >
                Flip Direction {sortDir === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-stone_brand cursor-pointer" data-testid="coverage-filter">
                <input
                  type="checkbox"
                  checked={minSources3}
                  onChange={(e) => setMinSources3(e.target.checked)}
                  className="accent-[#3f5c40]"
                  data-testid="coverage-filter-checkbox"
                />
                Show only stocks with 3+ sources (higher conviction)
              </label>
              <span className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light">
                Faded upside = 1-source coverage · treat as low conviction
              </span>
            </div>
            <div className="bg-white border border-line rounded-sm overflow-x-auto" data-testid="scanner-table">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line text-left bg-white">
                    <SortHeader label="Ticker"      col="ticker"             sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                    <SortHeader label="Live Price"  col="current_price"      sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                    <SortHeader label="Yahoo FV"    col="analyst_target_mean" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                    <SortHeader label="TickerTape"  col="tickertape_target"  sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                    <SortHeader label="M-Star FV"   col="user_morningstar_fv" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                    <SortHeader label="Consolidated FV" col="consolidated_fv" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                    <SortHeader label="Diff vs Live" col="diff"              sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric primary />
                    <th className="overline py-4 px-3 text-stone_brand-light whitespace-nowrap">Verdict</th>
                    <th className="overline py-4 px-3 text-stone_brand-light whitespace-nowrap"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => {
                    const cur = s.currency || "USD";
                    const mkt = marketFromTicker(s.ticker);
                    return (
                      <tr
                        key={s.ticker}
                        className="border-b border-line last:border-0 hover:bg-bone-100 transition-colors"
                        data-testid={`scanner-row-${s.ticker}`}
                      >
                        <td
                          className="py-3 px-3 cursor-pointer"
                          onClick={() => navigate(`/stock/${encodeURIComponent(s.ticker)}`)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-stone_brand-dark">
                              {displayTicker(s.ticker)}
                            </span>
                            <span className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1 rounded-sm">
                              {mkt}
                            </span>
                          </div>
                          <div className="text-xs text-stone_brand truncate max-w-[200px]">
                            {s.company_name}
                          </div>
                        </td>
                        <td
                          className="py-3 px-3 font-mono text-sm text-stone_brand-dark cursor-pointer"
                          onClick={() => navigate(`/stock/${encodeURIComponent(s.ticker)}`)}
                        >
                          {fmtMoney(s.current_price, cur)}
                          {s.day_change_percent !== null && s.day_change_percent !== undefined && (
                            <div
                              className={`text-[0.65rem] font-mono ${
                                s.day_change_percent > 0 ? "text-moss-light" : s.day_change_percent < 0 ? "text-terracotta" : "text-stone_brand"
                              }`}
                            >
                              {fmtPct(s.day_change_percent)}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                          {fmtMoney(s.analyst_target_mean, cur)}
                          {s.diff_analyst !== null && s.analyst_target_mean && (
                            <div className={`text-[0.65rem] font-mono ${marginColor(s.diff_analyst)}`}>
                              {fmtPct(s.diff_analyst)}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                          {s.tickertape_target ? (
                            <>
                              {fmtMoney(s.tickertape_target, cur)}
                              {s.diff_tickertape !== null && (
                                <div className={`text-[0.65rem] font-mono ${marginColor(s.diff_tickertape)}`}>
                                  {fmtPct(s.diff_tickertape)}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-stone_brand-light text-xs">—</span>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                          {editingMS === s.ticker ? (
                            <div className="flex items-center gap-1" data-testid={`edit-ms-${s.ticker}`}>
                              <Input
                                autoFocus
                                type="number"
                                step="0.01"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEditMS(s.ticker);
                                  if (e.key === "Escape") cancelEditMS();
                                }}
                                placeholder="₹ FV"
                                className="h-7 px-2 text-xs font-mono w-20 rounded-sm border-line"
                                data-testid={`edit-ms-input-${s.ticker}`}
                              />
                              <button
                                onClick={() => saveEditMS(s.ticker)}
                                disabled={savingMS}
                                className="p-1 text-moss hover:bg-moss-bg rounded-sm"
                                title="Save"
                                data-testid={`edit-ms-save-${s.ticker}`}
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={cancelEditMS}
                                className="p-1 text-stone_brand-light hover:text-terracotta hover:bg-terracotta-bg rounded-sm"
                                title="Cancel"
                                data-testid={`edit-ms-cancel-${s.ticker}`}
                              >
                                <XIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div
                              className="group inline-flex items-start gap-1 cursor-pointer"
                              onClick={() => startEditMS(s)}
                              title="Click to edit your Morningstar fair value"
                              data-testid={`ms-cell-${s.ticker}`}
                            >
                              {s.user_morningstar_fv ? (
                                <div>
                                  <div className="flex items-center gap-1">
                                    {fmtMoney(s.user_morningstar_fv, cur)}
                                    {s.user_morningstar_fv_overridden && (
                                      <span className="text-[0.5rem] font-mono text-amber_brand uppercase">·custom</span>
                                    )}
                                    <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 text-stone_brand transition-opacity" />
                                  </div>
                                  {s.diff_morningstar !== null && (
                                    <div className={`text-[0.65rem] font-mono ${marginColor(s.diff_morningstar)}`}>
                                      {fmtPct(s.diff_morningstar)}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-stone_brand-light text-xs flex items-center gap-1 hover:text-moss">
                                  <Pencil className="w-3 h-3" /> Set FV
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td
                          className="py-3 px-3 font-mono text-sm font-semibold text-moss cursor-pointer hover:underline underline-offset-4 decoration-dotted"
                          onClick={() => setSourcesStock(s)}
                          title="View all fair-value sources"
                          data-testid={`view-sources-${s.ticker}`}
                        >
                          {fmtMoney(s.consolidated_fv, cur)}
                          <div className="text-[0.65rem] font-mono text-stone_brand-light">
                            {s.source_count} source{s.source_count === 1 ? "" : "s"} · click
                          </div>
                        </td>
                        <td
                          className={`py-3 px-3 font-mono text-base font-semibold ${marginColor(s.diff)} ${s.source_count <= 1 ? "italic opacity-50" : ""}`}
                          title={s.source_count <= 1 ? "Only 1 source — low-conviction signal" : undefined}
                        >
                          {fmtPct(s.diff)}
                        </td>
                        <td className="py-3 px-3">
                          <ConvictionBadge conviction={s.conviction} />
                          <div className="mt-1">
                            <span
                              className={`px-2 py-0.5 rounded-sm text-[0.6rem] font-mono font-medium border ${verdictStyle(s.verdict)}`}
                            >
                              {s.verdict}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <button
                            onClick={() => handleRemove(s.ticker)}
                            className="p-1.5 text-stone_brand-light hover:text-terracotta hover:bg-terracotta-bg rounded-sm transition-colors"
                            data-testid={`remove-${s.ticker}`}
                            aria-label={`Remove ${s.ticker}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-[0.7rem] font-mono uppercase tracking-widest text-stone_brand-light mt-4">
              — Click any row for full AI digest, 1-year chart, and 9-platform external research links (Screener, Trendlyne, Value Research, Motilal Oswal, ET Money, INDmoney, Groww, Angel One, Zerodha).
            </p>
          </>
        )}
      </main>
      {sourcesStock && (
        <FairValueSourcesModal stock={sourcesStock} onClose={() => setSourcesStock(null)} />
      )}
    </div>
  );
}

function SortHeader({ label, col, sortBy, sortDir, onClick, numeric, primary }) {
  const active = sortBy === col;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      onClick={() => onClick(col)}
      className={`overline py-4 px-3 whitespace-nowrap cursor-pointer select-none transition-colors group ${
        active ? "text-moss" : "text-stone_brand-light hover:text-stone_brand-dark"
      } ${primary ? "bg-moss-bg/30" : ""}`}
      data-testid={`sort-${col}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <Icon className={`w-3 h-3 transition-opacity ${active ? "opacity-100" : "opacity-30 group-hover:opacity-70"}`} />
      </span>
    </th>
  );
}
